// Valeurs lisibles de la page publique des formulaires (récapitulatif après soumission, listes de fichiers).
// Fonctions pures, sans React ni réseau. Extrait de « adminvisastravelapp/src/formulaires/libelles.ts » (lot 4b :
// libelleQuestion, formaterDateIso, libelleValeur) complété des helpers de lecture des réponses du panneau admin
// « ReponsesFormulaire.tsx » (lot 5 : texteValeur) — mêmes formulations des deux côtés.
import { Question, REGEX, ValeurReponse, optionsCible } from './types';

// Libellé d'une question, ou son id si le libellé est vide ; `id` sert de repli quand la question est introuvable
export const libelleQuestion = (q: Question | undefined, id?: string): string => {
  if (q && q.libelle && q.libelle.trim()) return q.libelle.trim();
  if (q) return q.id;
  return id || '?';
};

// aaaa-mm-jj -> jj/mm/aaaa (toute autre chaîne est rendue telle quelle)
export const formaterDateIso = (v: string): string =>
  REGEX.dateIso.test(v) ? `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : v;

// Une valeur lisible selon la question : libellé de l'option (choix, choix_multiple, oui_non), date jj/mm/aaaa,
// nombre tel quel, texte entre guillemets ; la valeur brute si la question est inconnue.
export const libelleValeur = (cible: Question | undefined, v: string): string => {
  if (!cible) return v;
  const options = optionsCible(cible);
  if (options) {
    const o = options.filter((x) => x.valeur === v)[0];
    return o && o.libelle ? o.libelle : v;
  }
  if (cible.type === 'date') return formaterDateIso(v);
  if (cible.type === 'nombre') return v;
  return `« ${v} »`;
};

// Valeur brute d'une réponse -> texte lisible pour le récapitulatif : libellé d'option (choix, choix_multiple,
// oui_non), date jj/mm/aaaa, texte / nombre tels quels (sans guillemets) ; un tableau (choix_multiple) devient
// « a, b ». Chaîne vide si rien n'est renseigné (à afficher « — »).
export const texteValeur = (q: Question, v: ValeurReponse | undefined | null): string => {
  if (v === undefined || v === null) return '';
  const liste = Array.isArray(v) ? v : [v];
  const options = optionsCible(q);
  return liste
    .map((x) => {
      const s = String(x).trim();
      if (s === '') return '';
      if (options) return libelleValeur(q, s);
      if (q.type === 'date') return formaterDateIso(s);
      return s;
    })
    .filter((s) => s !== '')
    .join(', ');
};

// Taille d'un fichier lisible : « 184 ko », « 2,4 Mo » (au moins 1 ko pour un fichier non vide, « 0 ko » sinon)
export const tailleLisible = (octets: number): string => {
  const n = Number(octets);
  if (!Number.isFinite(n) || n <= 0) return '0 ko';
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
  return `${Math.max(1, Math.round(n / 1024))} ko`;
};
